/**
 * Shared helpers and constants for resolving issue type / priority / status
 * icons and colors from the Issue API configuration and icon lists.
 *
 * Ported from legacy tcweb topicsUtil.ts and topics.const.ts to provide parity
 * between the legacy Topics module and the new tc-issue-management UI library.
 */

import { translate } from '@fulcrum/lib';
import type {
  GetConfigurationResponse,
  ConfigurationDetails,
  IconsDetails,
} from '@data-access/connect/tc-issue-management';

/**
 * Normalized icon shape used for lookup in rendering helpers.
 */
export interface IssueIcon {
  readonly id: string;
  readonly iconContent: string;
}

/**
 * Default fallback color for priority / status when configuration is missing
 * and no well-known per-name palette applies. Intentionally dark so that
 * luminance-based text-color selection (see `IssueRightPanel`) picks white.
 */
export const DEFAULT_COLOR = '#22194D';

/**
 * Per-priority fallback palette used as the **second-tier fallback** when the
 * Issues API configuration has not loaded yet, failed, or doesn't assign a
 * color to a well-known priority name.
 *
 * Values ported from legacy tcweb topicsUtil / `IssueRightPanel`; kept in
 * sync with the 3D marker palette in `marker-visuals.ts` so 2D chips and 3D
 * markers don't drift when config is unavailable.
 *
 * Resolution order expected by consumers:
 *   1. Configured color (`configuration.priority[].color`)
 *   2. {@link DEFAULT_PRIORITY_COLORS} for the priority name
 *   3. {@link DEFAULT_COLOR}
 *
 * See spec §4.2 in `type_priority_icons_spec.md`.
 */
export const DEFAULT_PRIORITY_COLORS: Readonly<Record<string, string>> = {
  // eslint-disable-next-line @typescript-eslint/naming-convention -- API value
  Critical: '#D52A33',
  // eslint-disable-next-line @typescript-eslint/naming-convention -- API value
  High: '#FFA500',
  // eslint-disable-next-line @typescript-eslint/naming-convention -- API value
  Normal: '#6A6976',
  // eslint-disable-next-line @typescript-eslint/naming-convention -- API value
  Low: '#6A6976',
};

/**
 * Per-status fallback palette used as the **second-tier fallback** for the
 * status chip background when the Issues API configuration has not loaded,
 * failed, or doesn't assign a color to a well-known status name.
 *
 * Values ported from the legacy tcweb status-chip palette. See the priority
 * counterpart {@link DEFAULT_PRIORITY_COLORS} for the pattern this follows.
 *
 * Resolution order expected by consumers (see {@link getStatusColor}):
 *   1. Configured color (`configuration.status[].color`)
 *   2. {@link DEFAULT_STATUS_COLORS} for the status name
 *   3. {@link DEFAULT_COLOR}
 */
export const DEFAULT_STATUS_COLORS: Readonly<Record<string, string>> = {
  // eslint-disable-next-line @typescript-eslint/naming-convention -- API value
  New: 'rgb(54, 132, 209)',
  // eslint-disable-next-line @typescript-eslint/naming-convention -- API value
  'In Progress': 'rgb(252, 198, 3)',
  // eslint-disable-next-line @typescript-eslint/naming-convention -- API value
  Waiting: 'rgb(41, 179, 91)',
  // eslint-disable-next-line @typescript-eslint/naming-convention -- API value
  Done: 'rgb(54, 132, 209)',
  // eslint-disable-next-line @typescript-eslint/naming-convention -- API value
  Closed: 'rgb(128, 128, 128)',
};

/**
 * Default (well-known) icon ids provided by the Issue API. These are the same
 * ids the legacy tcweb Topics module relied on for fallback rendering.
 */
export const DEFAULT_TYPE_ICON_ID =
  'default:f47ac10b-58cc-4372-a567-0e02b2c3d479';
export const DEFAULT_STATUS_ICON_ID =
  'default:f3c0a8e1-2f6c-4d0c-90a0-9cb6e87b9d94';
export const DEFAULT_PRIORITY_ICON_ID =
  'default:ab9f6e21-ec0d-4788-8d1a-b3a5fd5b8212';

/**
 * Font-icon class names the API may return as an `iconId` value instead of a
 * prefixed UUID. When `getTypeIcon` resolves to one of these we should render
 * an `<i class="icon-font ...">` instead of an `<img>`.
 */
export const DEFAULT_TYPE_FONT_ICONS: readonly string[] = [
  'tc-icon-bcf',
  'tc-icon-comment',
  'tc-icon-error',
  'tc-icon-list-alt',
  'tc-icon-warning',
  'tc-icon-support',
  'tc-icon-rename',
  'tc-icon-clash-set',
  'tc-icon-check-circle',
  'tc-icon-bookmark',
  'tc-icon-circle',
];

/**
 * Looks up the base64 iconContent of an icon from a prefixed iconId like
 * `default:<uuid>` or `custom:<uuid>`.
 */
export function getIcon(
  iconsList: readonly IssueIcon[] | undefined,
  iconId: string | undefined
): string | undefined {
  if (!iconId) return undefined;
  const PREFIX_SEPARATOR = ':';
  const parts = iconId.split(PREFIX_SEPARATOR);
  const UUID_SEGMENT_INDEX = 1;
  const id = parts.length > 1 ? parts[UUID_SEGMENT_INDEX] : parts[0];
  const icon = iconsList?.find((item) => item.id === id);
  return icon?.iconContent;
}

/**
 * Returns the configured `iconId` for an issue type name (e.g.
 * `'default:<uuid>'`, `'custom:<uuid>'`, or a font-icon class name like
 * `'tc-icon-bcf'` — see {@link DEFAULT_TYPE_FONT_ICONS}), falling back to
 * {@link DEFAULT_TYPE_ICON_ID} when the type is missing or not configured.
 *
 * Unlike {@link getTypeIcon}, this returns the **raw** iconId before it is
 * stripped of its `default:`/`custom:` prefix — which is what
 * {@link isFontTypeIcon} needs to correctly detect a font-icon class.
 */
export function getTypeIconId(
  configuration: GetConfigurationResponse | null | undefined,
  typeName: string | undefined
): string {
  if (configuration && typeName) {
    const match = configuration.type?.find(
      (t: ConfigurationDetails) => t.name === typeName
    );
    if (match?.iconId) return match.iconId;
  }
  return DEFAULT_TYPE_ICON_ID;
}

/**
 * Returns the base64 iconContent for a given issue type name, falling back to
 * the configured default type icon when the type is not found.
 *
 * Note: when the configured iconId is a font-icon class (e.g. `'tc-icon-bcf'`)
 * rather than a prefixed UUID, this will return `undefined` since the class
 * name has no `iconContent` entry in `iconsList`. Prefer
 * {@link resolveTypeIconDisplay} in rendering code so both the image and
 * font-icon paths are handled consistently.
 */
export function getTypeIcon(
  configuration: GetConfigurationResponse | null | undefined,
  typeName: string | undefined,
  iconsList: readonly IssueIcon[] | undefined
): string | undefined {
  const iconId = getTypeIconId(configuration, typeName);
  return getIcon(iconsList, iconId);
}

/**
 * Tagged result of {@link resolveTypeIconDisplay} describing how a type icon
 * should be rendered — either as an `<img>` with a resolved `src`, or as an
 * `<i class="icon-font ...">` with a font-icon class.
 */
export type ResolvedTypeIcon =
  | { readonly kind: 'image'; readonly src: string }
  | { readonly kind: 'font'; readonly className: string };

/**
 * Resolves how an issue type icon should be rendered, choosing between a
 * base64 image (`<img>`) and a font-icon class (`<i class="icon-font …">`).
 *
 * Detection runs against the raw `iconId` from configuration (before the
 * `default:`/`custom:` prefix is stripped), so a configured font-icon class
 * like `'tc-icon-bcf'` is routed to the font-icon branch instead of being
 * looked up as a base64 content entry (which would always miss).
 *
 * Resolution order:
 *   1. If the configured iconId matches {@link DEFAULT_TYPE_FONT_ICONS} →
 *      render as a font icon using that class.
 *   2. Else look up the iconId's base64 `iconContent` in `iconsList` →
 *      render as an `<img>`.
 *   3. Else fall back to `fallbackFontIcon` (default `'tc-icon-bcf'`).
 */
export function resolveTypeIconDisplay(
  configuration: GetConfigurationResponse | null | undefined,
  typeName: string | undefined,
  iconsList: readonly IssueIcon[] | undefined,
  fallbackFontIcon: string = 'tc-icon-bcf'
): ResolvedTypeIcon {
  const iconId = getTypeIconId(configuration, typeName);
  if (isFontTypeIcon(iconId)) {
    return { kind: 'font', className: iconId };
  }
  const src = getIcon(iconsList, iconId);
  if (src) {
    return { kind: 'image', src };
  }
  return { kind: 'font', className: fallbackFontIcon };
}

/**
 * Primitive lookup: returns the raw configured color for a priority, or
 * `undefined` when no configuration is available, no matching priority is
 * found, or the matching entry has no color field.
 *
 * Prefer this when the caller wants to layer its own fallback (e.g. a
 * per-priority palette for 3D markers, or an interaction-state override).
 * Callers that just need "configured color or DEFAULT_COLOR" should use
 * {@link getPriorityColor}.
 */
export function getConfiguredPriorityColor(
  configuration: GetConfigurationResponse | null | undefined,
  priorityName: string | undefined
): string | undefined {
  if (!configuration || !priorityName) return undefined;
  return configuration.priority?.find(
    (p: ConfigurationDetails) => p.name === priorityName
  )?.color;
}

/**
 * Returns the color to render for a priority, applying the spec §4.2
 * resolution order:
 *   1. Configured color from the Issues API (`configuration.priority[].color`).
 *   2. Well-known per-priority palette in {@link DEFAULT_PRIORITY_COLORS}
 *      (Critical/High/Normal/Low), so chips stay visually distinct during
 *      initial load, API errors, or when a configured priority lacks a color.
 *   3. {@link DEFAULT_COLOR} as the final fallback for unknown priority names.
 */
export function getPriorityColor(
  configuration: GetConfigurationResponse | null | undefined,
  priorityName: string | undefined
): string {
  const configured = getConfiguredPriorityColor(configuration, priorityName);
  if (configured) return configured;
  if (priorityName && DEFAULT_PRIORITY_COLORS[priorityName]) {
    return DEFAULT_PRIORITY_COLORS[priorityName];
  }
  return DEFAULT_COLOR;
}

/**
 * Primitive lookup: returns the raw configured color for a status, or
 * `undefined` when no configuration is available, no matching status is
 * found, or the matching entry has no color field.
 *
 * Prefer this when the caller wants to layer its own fallback (e.g. a
 * per-status palette so chips remain visually distinct during initial load
 * or when config is missing). Callers that just need "configured color or
 * DEFAULT_COLOR" should use {@link getStatusColor}.
 */
export function getConfiguredStatusColor(
  configuration: GetConfigurationResponse | null | undefined,
  statusName: string | undefined
): string | undefined {
  if (!configuration || !statusName) return undefined;
  return configuration.status?.find(
    (s: ConfigurationDetails) => s.name === statusName
  )?.color;
}

/**
 * Returns the color to render for a status chip, applying the same 3-tier
 * resolution as {@link getPriorityColor}:
 *   1. Configured color from the Issues API (`configuration.status[].color`).
 *   2. Well-known per-status palette in {@link DEFAULT_STATUS_COLORS}
 *      (New / In Progress / Waiting / Done / Closed), so chips stay visually
 *      distinct during initial load, API errors, or when a configured status
 *      lacks a color field.
 *   3. {@link DEFAULT_COLOR} as the final fallback for unknown status names.
 */
export function getStatusColor(
  configuration: GetConfigurationResponse | null | undefined,
  statusName: string | undefined
): string {
  const configured = getConfiguredStatusColor(configuration, statusName);
  if (configured) return configured;
  if (statusName && DEFAULT_STATUS_COLORS[statusName]) {
    return DEFAULT_STATUS_COLORS[statusName];
  }
  return DEFAULT_COLOR;
}

/**
 * Parse a CSS color string into its 0-255 RGB components. Supports the
 * formats the Issues API configuration and our local fallbacks emit:
 *   - `#RRGGBB` and `#RGB` hex
 *   - `rgb(R, G, B)` and `rgba(R, G, B, A)` with integer channels
 *
 * Returns `null` for any unsupported format (e.g. named colors, `hsl()`,
 * percentage channels) so callers can choose a safe default.
 */
export function parseCssColorToRgb(
  color: string
): { r: number; g: number; b: number } | null {
  const trimmed = color.trim();
  const HEX_RADIX = 16;
  const DECIMAL_RADIX = 10;

  const hex6 = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(trimmed);
  if (hex6) {
    const [, r, g, b] = hex6;
    return {
      r: parseInt(r, HEX_RADIX),
      g: parseInt(g, HEX_RADIX),
      b: parseInt(b, HEX_RADIX),
    };
  }
  const hex3 = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(trimmed);
  if (hex3) {
    const [, r, g, b] = hex3;
    return {
      r: parseInt(r + r, HEX_RADIX),
      g: parseInt(g + g, HEX_RADIX),
      b: parseInt(b + b, HEX_RADIX),
    };
  }
  const rgb = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(trimmed);
  if (rgb) {
    const [, r, g, b] = rgb;
    return {
      r: parseInt(r, DECIMAL_RADIX),
      g: parseInt(g, DECIMAL_RADIX),
      b: parseInt(b, DECIMAL_RADIX),
    };
  }
  return null;
}

/**
 * Picks a readable text color (`'white'` or `'black'`) for a status chip by
 * computing the relative luminance of the resolved background color from
 * {@link getStatusColor}. Dark backgrounds render white text, light
 * backgrounds render black text. Returns `'black'` when the background color
 * cannot be parsed, to avoid illegible white-on-white.
 */
export function getStatusTextColor(
  configuration: GetConfigurationResponse | null | undefined,
  statusName: string | undefined
): 'white' | 'black' {
  const parsed = parseCssColorToRgb(getStatusColor(configuration, statusName));
  if (!parsed) return 'black';

  const LUMINANCE_R_COEFFICIENT = 0.299;
  const LUMINANCE_G_COEFFICIENT = 0.587;
  const LUMINANCE_B_COEFFICIENT = 0.114;
  const MAX_RGB_VALUE = 255;
  const LUMINANCE_THRESHOLD = 0.5;
  const luminance =
    (LUMINANCE_R_COEFFICIENT * parsed.r +
      LUMINANCE_G_COEFFICIENT * parsed.g +
      LUMINANCE_B_COEFFICIENT * parsed.b) /
    MAX_RGB_VALUE;
  return luminance < LUMINANCE_THRESHOLD ? 'white' : 'black';
}

/**
 * Builds a normalized IssueIcon[] from the union of default + custom icons.
 * Entries without a valid id or iconContent are filtered out.
 */
export function buildIconsList(
  defaults?: readonly IconsDetails[] | null,
  customs?: readonly IconsDetails[] | null
): IssueIcon[] {
  const combined: IconsDetails[] = [...(defaults ?? []), ...(customs ?? [])];
  const result: IssueIcon[] = [];
  for (const icon of combined) {
    if (icon.id && icon.iconContent) {
      result.push({ id: icon.id, iconContent: icon.iconContent });
    }
  }
  return result;
}

/**
 * Returns the translated label for well-known issue type names, or the raw
 * value for user-defined types.
 */
export function getTypeTranslation(type: string | undefined): string {
  if (!type) return '';
  switch (type) {
    case 'Undefined':
      return translate('TC.Common.Type.Undefined');
    case 'Comment':
      return translate('TC.Common.Type.Comment');
    case 'Issue':
      return translate('TC.Common.Type.Issue');
    case 'Request':
      return translate('TC.Common.Type.Request');
    case 'Fault':
      return translate('TC.Common.Type.Fault');
    case 'Inquiry':
      return translate('TC.Common.Type.Inquiry');
    case 'Solution':
      return translate('TC.Common.Type.Solution');
    case 'Remark':
      return translate('TC.Common.Type.Remark');
    case 'Clash':
      return translate('TC.Common.Type.Clash');
    default:
      return type;
  }
}

/**
 * Returns the translated label for well-known priority names, or the raw
 * value for user-defined priorities.
 */
export function getPriorityTranslation(priority: string | undefined): string {
  if (!priority) return '';
  switch (priority) {
    case 'Low':
      return translate('TC.Common.Priority.Low');
    case 'Normal':
      return translate('TC.Common.Priority.Normal');
    case 'High':
      return translate('TC.Common.Priority.High');
    case 'Critical':
      return translate('TC.Common.Priority.Critical');
    default:
      return priority;
  }
}

/**
 * Returns the translated label for well-known issue status names, or the raw
 * value for user-defined statuses.
 *
 * Note: the `'New'` status maps to the legacy `'NEW'` translation key (not
 * `'TC.Common.Status.New'`) for parity with the existing tcweb Topics module.
 * Normalising that key is a separate i18n follow-up.
 */
export function getStatusTranslation(status: string | undefined): string {
  if (!status) return '';
  switch (status) {
    case 'New':
      return translate('NEW');
    case 'In Progress':
      return translate('TC.Common.Status.InProgress');
    case 'Waiting':
      return translate('TC.Common.Status.Waiting');
    case 'Done':
      return translate('TC.Common.Status.Done');
    case 'Closed':
      return translate('TC.Common.Status.Closed');
    default:
      return status;
  }
}

/**
 * Indicates whether the given `iconId` is a known font-icon class name (e.g.
 * `'tc-icon-bcf'`). When true, the caller should render an
 * `<i class="icon-font ...">` using that class rather than trying to look up
 * a base64 `iconContent`.
 *
 * **Important:** this must be called with an `iconId` (as returned by
 * {@link getTypeIconId}) — **not** with the `iconContent` returned by
 * {@link getTypeIcon}, which is a base64 data URL and will never match.
 * Most call sites should prefer {@link resolveTypeIconDisplay} which handles
 * the detection internally.
 */
export function isFontTypeIcon(resolved: string | undefined): boolean {
  if (!resolved) return false;
  return DEFAULT_TYPE_FONT_ICONS.includes(resolved);
}
